import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse } from "../../../types/common";
import { IBillingInvoice } from "../../../types/api/company";
import { listBillingInvoices, getBillingInvoicePdf } from "./callbacks";
import axiosInstance from "../../../lib/axios";

export const useCompanyBillingInvoices = (
  options?: Omit<UseQueryOptions<TResponse<IBillingInvoice[]>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<IBillingInvoice[]>, Error> => {
  return useQuery<TResponse<IBillingInvoice[]>, Error>({
    queryKey: [API_ENDPOINTS.BILLING_INVOICES],
    queryFn: async () => {
      const res = await listBillingInvoices();
      return res.data;
    },
    staleTime: 120000,
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });
};

export const useGetBillingInvoicePdf = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<{ pdf_url: string }>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<{ pdf_url: string }>, unknown, number> = {}
): UseMutationResult<TResponse<{ pdf_url: string }>, unknown, number> => {
  return useMutation<TResponse<{ pdf_url: string }>, unknown, number>({
    mutationFn: async (id: number) => {
      const res = await getBillingInvoicePdf(id);
      return res.data;
    },
    onSuccess: (res, ...rest) => {
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useCancelAccount = () => {
  return useMutation({
    mutationFn: (data: { reason: string; survey_answers?: Record<string, string> }) =>
      axiosInstance.post("/billing/cancel-account", data),
  });
};

export const useUpdatePlanType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (plan_type: "monthly" | "annual") =>
      axiosInstance.post("/billing/update-plan-type", { plan_type }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [API_ENDPOINTS.MY_COMPANY] }),
  });
};
